/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ContractResponse } from '../models/contract-response';
import { CreateContractRequest } from '../models/create-contract-request';
import { GenericResponse } from '../models/generic-response';
import { GetContractResponse } from '../models/get-contract-response';

/**
 * Contract APIs
 */
@Injectable({ providedIn: 'root' })
export class ContractService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllContracts()` */
  static readonly GetAllContractsPath = '/api/v1/contracts';

  /**
   * Get list of Contract.
   *
   * Gets the list of contracts.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllContracts()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllContracts$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - contractNumber
       */
      search?: string;

      /**
       * This enables filtering of contracts based on the quotation id.
       */
      quotation?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetContractResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContractService.GetAllContractsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('quotation', params.quotation, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetContractResponse>;
        })
      );
  }

  /**
   * Get list of Contract.
   *
   * Gets the list of contracts.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllContracts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllContracts(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields:
       * - contractNumber
       */
      search?: string;

      /**
       * This enables filtering of contracts based on the quotation id.
       */
      quotation?: number;
    },
    context?: HttpContext
  ): Observable<GetContractResponse> {
    return this.getAllContracts$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetContractResponse>): GetContractResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `createContract()` */
  static readonly CreateContractPath = '/api/v1/contracts';

  /**
   * create Contract.
   *
   * Creates a contract
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createContract()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContract$Response(
    params: {
      body: CreateContractRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContractResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContractService.CreateContractPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContractResponse>;
        })
      );
  }

  /**
   * create Contract.
   *
   * Creates a contract
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createContract$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContract(
    params: {
      body: CreateContractRequest;
    },
    context?: HttpContext
  ): Observable<ContractResponse> {
    return this.createContract$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContractResponse>): ContractResponse => r.body)
    );
  }

  /** Path part for operation `getContractById()` */
  static readonly GetContractByIdPath = '/api/v1/contracts/{id}';

  /**
   * fetch Contract details.
   *
   * Gets the details of a contract.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContractById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContractById$Response(
    params: {
      /**
       * The Contract id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ContractResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContractService.GetContractByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ContractResponse>;
        })
      );
  }

  /**
   * fetch Contract details.
   *
   * Gets the details of a contract.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContractById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContractById(
    params: {
      /**
       * The Contract id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ContractResponse> {
    return this.getContractById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ContractResponse>): ContractResponse => r.body)
    );
  }

  /** Path part for operation `deleteContractById()` */
  static readonly DeleteContractByIdPath = '/api/v1/contracts/{id}';

  /**
   * Delete Contract  by id.
   *
   * Marks a contract as deleted.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteContractById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteContractById$Response(
    params: {
      /**
       * The Contract id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ContractService.DeleteContractByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete Contract  by id.
   *
   * Marks a contract as deleted.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteContractById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteContractById(
    params: {
      /**
       * The Contract id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteContractById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }
}
